import React, { useEffect } from 'react';

import queryString from 'query-string';
import { connect } from 'react-redux';
import { Redirect, useHistory, useLocation } from 'react-router';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import { accountsByIdStatusSelector, accountsSelector } from '../account/account-selectors';
import { loadAccounts } from '../account/actions/load-accounts';
import { accountGroupsForUsernameSelector } from '../account-group/account-group-selectors';
import { loadAccountGroups } from '../account-group/actions/load-account-groups';
import { loadOrg } from '../organization/actions/load-org';
import { userSelector } from '../reducers/current-user-selectors';
import { lastVisitedOrgIdSelector } from '../reducers/display-settings-selectors';
import { orgSelector, orgStatusSelector } from '../reducers/org-selectors';

import { applicantDirectoryRoute, HOME_ROUTE } from '../web-routes';

import { ApplicantDirectory } from './applicant-directory';

import { parseRequiredId } from '../util/parse-required-id';
import { HIRING_PERMISSION } from '../util/permissions';
import { LoadingMessage } from '../widgets/loading-message';

const mapStateToProps = (state: any) => {
  const user = userSelector(state);
  const lastVisitedOrgId = user?.username ? lastVisitedOrgIdSelector(state, user.username) : null;
  const org = lastVisitedOrgId ? orgSelector(state, lastVisitedOrgId) : null;
  const orgState = lastVisitedOrgId ? orgStatusSelector(state, lastVisitedOrgId) : null;
  const accounts = accountsSelector(state);
  const accountState = accountsByIdStatusSelector(state);
  const allowedAccountIds = user.accountGroupId
    ? accountGroupsForUsernameSelector(state, user.username).flatMap((ag) =>
        ag.Accounts.map((a) => a.accountId),
      )
    : [user.accountId];

  return {
    user,
    lastVisitedOrgId,
    org,
    orgState,
    accounts,
    accountState,
    allowedAccountIds,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, AnyAction>) => ({
  actions: {
    loadAccounts: () => dispatch(loadAccounts()),
    loadOrg: (orgId: number) => dispatch(loadOrg(orgId)),
    loadAccountGroups: () => dispatch(loadAccountGroups()),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = ReduxProps & DispatchProps;

export const ApplicantDirectoryRedirect = connect(
  mapStateToProps,
  mapDispatchToProps,
)((props: Props) => {
  const {
    actions,
    lastVisitedOrgId,
    org,
    orgState,
    accounts,
    accountState,
    user,
    allowedAccountIds,
  } = props;
  const history = useHistory();
  const { search } = useLocation();
  const queryParams = queryString.parse(search);

  const parsedAccountId = parseRequiredId(queryParams.accountId);
  const parsedOrgId = parseRequiredId(queryParams.orgId);

  useEffect(() => {
    if (user?.accountGroupId) {
      actions.loadAccountGroups();
    }
    if (lastVisitedOrgId) {
      actions.loadOrg(lastVisitedOrgId);
    }
    actions.loadAccounts();
  });

  if (!allowedAccountIds || !allowedAccountIds.length) {
    return <LoadingMessage />;
  }

  const hasHiringPermissions = user.hiringPermission === HIRING_PERMISSION.ALL;
  const userHasPermissions =
    (user.isAdmin || allowedAccountIds.includes(parsedAccountId)) && hasHiringPermissions;

  if (!hasHiringPermissions) {
    return <Redirect to={HOME_ROUTE} />;
  }

  if (userHasPermissions && parsedAccountId && parsedOrgId) {
    // if we have an accountId and orgId that the user has permission to view, go to the applicant directory
    return <ApplicantDirectory accountId={parsedAccountId} lastVisitedOrgId={parsedOrgId} />;
  }

  if (accountState?.loading || !accounts || !accounts.length || orgState?.loading) {
    return <LoadingMessage />;
  }

  const accountsWithOrgs = accounts.filter((a) => !!a.Organizations && !!a.Organizations.length);

  if (!accountsWithOrgs.length) {
    return <Redirect to={HOME_ROUTE} />;
  }
  const lastVisitedAccountId = org?.accountId || accountsWithOrgs[0].accountId;
  const orgId = lastVisitedOrgId || accountsWithOrgs[0].Organizations[0].org_id;

  if (!lastVisitedAccountId) {
    // we need an accountId for the directory so if we can't find one, return home
    return <Redirect to={HOME_ROUTE} />;
  }

  // If we are here, this should be the first time we are navigating to the applicant directory in a session - set the account and org then redirect

  // The first time a user hits the applicant directory page (through the search bar) we want to default to the org the user was just in for faster load times
  // the user can opt to view across the entire account by clearing this filter
  const updatedQueryParams = {
    ...queryParams,
    accountId: lastVisitedAccountId,
    orgId,
    filterByOrgId: orgId,
  };
  history.replace(applicantDirectoryRoute(updatedQueryParams));
  return null;
});
